import React, { useEffect, useState } from 'react';
import { Mold, ProductPart, Insert, FormFieldConfig } from '../../types/mold';
import { Info } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import FormFieldWrapper from '../shared/FormFieldWrapper';
import ProductPartManager from '../shared/ProductPartManager';
import InsertManager from '../shared/InsertManager';
import { styleConfig } from '../../constants/design';
import { getNestedValue } from '../../utils/object';

interface ExtendedMold extends Mold {
    customerId?: number;
}

interface NewMoldInformationProps {
    mold: Mold;
    editedMold: ExtendedMold;
    isEditing: boolean;
    onFieldChange: (field: string, value: any) => void;
    layout?: 'default' | 'expanded';
    fieldErrors?: Record<string, boolean>;
    viewMode?: 'tabs' | 'review';
    currentStep?: number;
    showTabListButton?: boolean;
    moldFormConfiguration: Record<string, FormFieldConfig[]>;
}

export type MoldTabKey = 'general' | 'insert' | 'part';

const NewMoldInformation: React.FC<NewMoldInformationProps> = ({
    editedMold,
    isEditing,
    onFieldChange,
    layout = 'default',
    fieldErrors = {},
    viewMode = 'tabs',
    currentStep = 1,
    showTabListButton = false,
    moldFormConfiguration,
}) => {
    const isReviewing = viewMode === 'review'
    // Map currentStep to activeTab
    const getActiveTabFromStep = (step: number): MoldTabKey => {
        switch (step) {
            case 1: return 'part';
            case 2: return 'general';
            case 3: return 'insert';
            default: return 'part';
        }
    };

    const [activeTab, setActiveTab] = useState<MoldTabKey>(getActiveTabFromStep(currentStep));
    const { customers } = useCustomers();
    const [productParts, setProductParts] = useState<ProductPart[]>([]);
    const [inserts, setInserts] = useState<Insert[]>([]);

    // Update activeTab when currentStep changes
    React.useEffect(() => {
        if (viewMode === 'tabs') {
            setActiveTab(getActiveTabFromStep(currentStep));
        }
    }, [currentStep, viewMode]);

    const handleCustomerChange = (value: string) => {
        const newCustomer = customers.find(customer => customer.id === Number(value));
        if (newCustomer) {
            onFieldChange('customer', newCustomer);
        }
    };

    const TabButton: React.FC<{ value: MoldTabKey, label: string, icon: React.ElementType }> = ({ value, label, icon: Icon }) => (
        <button
            onClick={() => setActiveTab(value)}
            className={`flex-1 py-3 px-4 text-base font-medium flex items-center justify-center ${activeTab === value
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
            disabled={viewMode === 'tabs'} // Disable manual tab switching when controlled by currentStep
        >
            <Icon className="mr-2" size={20} />
            <p className="hidden sm:block">{label}</p>
        </button>
    );

    const getGridSpace = () => {
        if (!isEditing) return 'gap-0';
        if (layout === 'default') {
            return 'gap-1';
        }
        return 'gap-4';
    }
    const gridClasses = `grid grid-cols-1 md:grid-cols-2 ${getGridSpace()}`;

    const getValue = (key: string) => {
        if (key === 'customerId') {
            const customerId = editedMold.customerId ?? editedMold.customer?.id;
            const customer = customers.find(customer => Number(customer.id) === Number(customerId));
            return customer ? customer.id : '';
        }
        return getNestedValue(editedMold, key);
    }

    // Track previous showIf state for each field
    const prevShowIfRef = React.useRef<Record<string, boolean>>({});

    useEffect(() => {
        // For all fields in all tabs
        Object.entries(moldFormConfiguration).forEach(([_tabKey, fields]) => {
            fields.forEach(field => {
                if (typeof field.showIf === 'function') {
                    const key = String(field.key);
                    const prev = prevShowIfRef.current[key];
                    const now = field.showIf(editedMold);
                    // If it was visible and now hidden, reset
                    if (prev === true && now === false) {
                        if (getValue(key) !== undefined && getValue(key) !== null) {
                            onFieldChange(key, undefined);
                        }
                    }
                    prevShowIfRef.current[key] = now;
                }
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedMold]);

    const renderTabContent = (tabKey: MoldTabKey) => {
        return (
            <div className={isReviewing ? 'mb-8' : ''}>
                {isReviewing && (
                    <div className="mb-4">
                        <h3 className="text-xl font-semibold text-blue-600 flex items-center">
                            {tabKey === 'general' && <><Info className="mr-2" size={20} />ข้อมูลแม่พิมพ์เบื้องต้น</>}
                            {tabKey === 'part' && <>{styleConfig.part.icon && <styleConfig.part.icon className="mr-2" size={20} />}{styleConfig.part.label}</>}
                            {tabKey === 'insert' && <>{styleConfig.insert.icon && <styleConfig.insert.icon className="mr-2" size={20} />}{styleConfig.insert.label}</>}
                        </h3>
                        <div className="h-px bg-gray-200 mt-2 mb-4"></div>
                    </div>
                )}

                {['insert'].includes(tabKey) && (
                    <div className={`rounded-xl mb-5 ${isEditing ? 'mt-5 mx-3 pt-3 pb-5 ' : ''}`}>
                        <InsertManager
                            title={isReviewing ? "อินเสิร์ทที่เกี่ยวข้อง" : "เพิ่มรายการอินเสิร์ทที่เกี่ยวข้อง"}
                            mode="selection"
                            inserts={inserts}
                            onInsertsChange={(newInserts) => {
                                setInserts(newInserts);
                                onFieldChange('inserts', newInserts);
                            }}
                            showDeleteButton={isEditing}
                            showEditButton={isEditing}
                            showActionButtons={isEditing}
                        />
                    </div>
                )}

                <div className={`${gridClasses} pt-5`}>
                    {moldFormConfiguration[tabKey]
                        .filter((field) => {
                            return !field.showIf || field.showIf(editedMold);
                        })
                        .map((field) => (
                            <FormFieldWrapper
                                key={field.key as string | number}
                                displayStyle='inline-expanded'
                                isEditing={isEditing}
                                config={field}
                                value={getValue(field.key as string)}
                                onChange={(value) => {
                                    if (field.key === 'customerId') {
                                        handleCustomerChange(value);
                                    }
                                    const formattedValue = field.type === 'number' ? Number(value) : value;
                                    onFieldChange(field.key as keyof ExtendedMold, formattedValue)
                                }}
                                disabled={field.disabled}
                                hasError={fieldErrors?.[field.key as string] || false}
                            />
                        ))}
                </div>
                {['part'].includes(tabKey) && (
                    <div className={`${isEditing ? 'mt-0 sm:mt-5 mb-4 mx-0 sm:mx-3' : ''}`}>
                        <ProductPartManager
                            title={isReviewing ? "ชิ้นส่วนที่เกี่ยวข้อง" : "เพิ่มรายการชิ้นส่วนที่เกี่ยวข้อง"}
                            mode="selection"
                            productParts={productParts}
                            onPartsChange={(parts) => {
                                setProductParts(parts);
                                onFieldChange('productParts', parts);
                            }}
                            showDeleteButton={isEditing}
                            showEditButton={isEditing}
                            showActionButtons={isEditing}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex-grow">
            {viewMode === 'tabs' && showTabListButton && (
                <div className={`flex border-b ${layout === 'expanded' ? 'mb-5' : 'mb-2'}`}>
                    <TabButton value="general" label="ข้อมูลแม่พิมพ์เบื้องต้น" icon={Info} />
                    <TabButton value="part" label={styleConfig.part.label} icon={styleConfig.part.icon} />
                    <TabButton value="insert" label={styleConfig.insert.label} icon={styleConfig.insert.icon} />
                </div>
            )}

            {isReviewing ? (
                // Review mode: show all tabs in one view
                <div className="space-y-6">
                    {Object.keys(moldFormConfiguration).map((tabKey) => (
                        <React.Fragment key={tabKey}>
                            {renderTabContent(tabKey as MoldTabKey)}
                        </React.Fragment>
                    ))}
                </div>
            ) : (
                // Tab mode: show only active tab based on currentStep
                <div>
                    {renderTabContent(activeTab)}
                </div>
            )}
        </div>
    );
}

export default NewMoldInformation;
